import { useCallback } from 'react';
import { domainsApi } from '@/api/domains';
import { useResource } from '@/hooks/useResource';
import type {
  CreateDomainRequest,
  CreateDomainResponse,
  DNSValidationResponse,
  Domain,
  DomainFilters,
  UpdateDomainRequest,
} from '@/types/domain';

/**
 * Domain list + domain operations
 */
export function useDomains(filters: DomainFilters = {}) {
  const { node_id, status, search } = filters;

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await domainsApi.getAll({ node_id, status, search }, { signal });
      return response.domains ?? [];
    },
    [node_id, status, search]
  );

  const resource = useResource<Domain[]>(fetcher, { errorMessage: 'Failed to load domains' });
  const { setData, reload } = resource;

  /**
   * Create a domain; the list is reloaded to get the full record
   */
  const createDomain = useCallback(
    async (data: CreateDomainRequest): Promise<CreateDomainResponse> => {
      const created = await domainsApi.create(data);
      void reload();
      return created;
    },
    [reload]
  );

  const updateDomain = useCallback(
    async (id: string, data: UpdateDomainRequest): Promise<Domain> => {
      const updated = await domainsApi.update(id, data);
      setData((prev) => prev?.map((domain) => (domain.id === id ? updated : domain)));
      return updated;
    },
    [setData]
  );

  const deleteDomain = useCallback(
    async (id: string): Promise<void> => {
      await domainsApi.delete(id);
      setData((prev) => prev?.filter((domain) => domain.id !== id));
    },
    [setData]
  );

  /**
   * Validate DNS; marks the domain validated locally on success
   */
  const validateDns = useCallback(
    async (id: string): Promise<DNSValidationResponse> => {
      const result = await domainsApi.validateDNS(id);
      if (result.validated) {
        setData((prev) =>
          prev?.map((domain) =>
            domain.id === id
              ? { ...domain, dns_validated: true, dns_validated_at: new Date().toISOString() }
              : domain
          )
        );
      }
      return result;
    },
    [setData]
  );

  return {
    domains: resource.data ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    createDomain,
    updateDomain,
    deleteDomain,
    validateDns,
  };
}
